import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';
import { statutoryBookName } from '../../utils/countryFeatures';
import DraftEntries from './DraftEntries';
import ChartOfAccounts from './ChartOfAccounts';
import VendorAccounts from './VendorAccounts';
import ManualEntry from './ManualEntry';
import FixedAssets from './FixedAssets';
import EntityInfo from './EntityInfo';
import CompliancePanel from './CompliancePanel';
import TaxLedgers from './TaxLedgers';
import Registers from './Registers';
import Headless from './Headless';

// ── Statutory books (Accounting Act B.E. 2543) ──────────────────────────────────────────────────
// The printable side of the ledger: the five daily journals, the general ledger and the trial
// balance, chosen by accounting period.
//
// Kept in its own file rather than added to Bookkeeping/index.tsx, which is already 697 lines, and
// because the whole accounting module has to lift back into mailflow-src as a unit.
//
// Books open in a new tab instead of downloading. An accountant reads a journal far more often than
// they file one, and a download that lands in ~/Downloads is a worse default for that.
//
// ── On the three languages ──────────────────────────────────────────────────────────────────────
// The OPERATING layer of this screen is en/zh/th; the accounting content is not. Every Thai string
// left as a literal below is the statutory NAME of a book, a statement or a form — บัญชีเงินสด,
// งบทดลอง, ภ.ง.ด.50 — and it is what the server prints on the document face. An operator working in
// Chinese still has to see which Thai document they are about to produce, so those names do not
// move. What moves is the chrome around them: the English gloss under each card, the buttons, the
// captions, the explanatory paragraphs.
//
// ⛔ The card tuples below are `as const`, and that matters: putting t(...) inside one changes the
// inferred tuple type and breaks the `([k, th, enKey, path])` destructuring at the call site. So the
// tuple carries a KEY and t() is called where the card renders, never inside the array.

type Period = { id: string; start_date: string; end_date: string; status: string; entries: number };
type Book = { kind: string; label: string; bankAccountId: string; entries: number };

// Statutory book names — the country pack's (utils/countryFeatures.ts; Thailand: the DBD's own), identical in
// every locale. ⚠️ These are the LONG forms; DraftEntries.tsx holds the SHORT ones (เงินสด, รายวันซื้อ …) under
// different keys. Different strings, different keys — one shared key would print the wrong name in one of the two screens.
const baht = (satang: any) => {
  const n = Number(satang) || 0;
  return `${money(Math.abs(n) / 100)}${n < 0 ? ' (–)' : ''}`;
};
const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };

const card: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', borderRadius: 10,
  border: '1px solid var(--border)', background: 'var(--bg-surface)', cursor: 'pointer',
  textAlign: 'left', width: '100%',
};

const fieldStyle: React.CSSProperties = {
  padding: '7px 10px', fontSize: 12.5, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none',
};
const actionStyle: React.CSSProperties = {
  padding: '7px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};

/** Opening the very first period — and every one after it. s.10's twelve months is the default, so
 *  the ordinary case is a start date and nothing else.
 *  Exported (owner 2026-10-13) so the company workspace (CompanyWorkspace/) can mount it per submenu
 *  instead of collapsing the whole page to this form the way StatutoryBooks does. Behaviour unchanged. */
export function OpenPeriodForm({ clientId, onOpened }: { clientId: string; onOpened: () => void }) {
  const { t } = useTranslation();
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    setBusy(true); setErr('');
    try { await api.acctOpenPeriod({ clientId, startDate: start, endDate: end || undefined }); onOpened(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div style={{ padding: 20, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
        เปิดรอบระยะเวลาบัญชี · {t('pa_sb_open_books_gloss')}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.6, marginBottom: 12 }}>
        {t('pa_sb_open_books_why')}
      </div>
      <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_sb_from')}</label>
        <input type="date" value={start} onChange={e => setStart(e.target.value)} style={fieldStyle} />
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_sb_to')}</label>
        <input type="date" value={end} onChange={e => setEnd(e.target.value)} placeholder={t('pa_sb_optional')} style={fieldStyle} />
        <button onClick={go} disabled={busy} style={{ ...actionStyle, background: 'var(--accent)', color: '#fff', border: 'none', opacity: busy ? 0.6 : 1 }}>
          <i className={busy ? 'ti ti-loader' : 'ti ti-book'} aria-hidden /> {t('pa_sb_open_period')}
        </button>
      </div>
      {err && <div style={{ marginTop: 10, fontSize: 12.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', padding: '8px 12px', borderRadius: 8 }}>{err}</div>}
    </div>
  );
}

/** Closing. Shows what the year would close at BEFORE it happens, because there is no reopen.
 *  Exported (owner 2026-10-13) for the workspace's Period Closing submenu; the translated confirm stays. */
export function ClosePeriod({ periodId, onClosed }: { periodId: string; onClosed: () => void }) {
  const { t } = useTranslation();
  const [pv, setPv] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<any>(null);
  const preview = async () => {
    setBusy(true); setErr('');
    try { setPv(await api.acctClosePreview(periodId)); } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const close = async () => {
    // The one irreversible button on this screen, so its warning is translated like every other
    // operator string. An accountant working in the Chinese UI must get the warning, not a
    // paragraph of English they skim past on the way to OK.
    if (!window.confirm(t('pa_sb_close_confirm'))) return;
    setBusy(true); setErr('');
    try { setDone(await api.acctClosePeriod(periodId)); onClosed(); } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const baht = (s: number) => money(Number(s || 0) / 100);
  if (done) {
    return (
      <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', border: '1px solid #BBE5CB', padding: '10px 13px', borderRadius: 9 }}>
        <b>ปิดบัญชีแล้ว.</b>{' '}
        {t('pa_sb_closed_detail', {
          entries: done.entries,
          result: done.profit >= 0 ? t('pa_sb_profit') : t('pa_sb_loss'),
          amount: baht(Math.abs(done.profit)),
          carried: done.carriedForward.length,
          vouchers: Object.values(done.stamped || {}).reduce((a: any, b: any) => a + b, 0),
        })}
      </div>
    );
  }
  return (
    <div>
      {!pv ? (
        <button onClick={preview} disabled={busy} style={actionStyle}>
          <i className={busy ? 'ti ti-loader' : 'ti ti-lock'} aria-hidden /> ปิดบัญชี · {t('pa_sb_close_gloss')}
        </button>
      ) : (
        <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 9, background: 'var(--bg-app)' }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.7 }}>
            {t('pa_sb_close_sum', { income: baht(pv.income), expense: baht(pv.expense) })}{' '}
            <b>{pv.profit >= 0 ? t('pa_sb_profit') : t('pa_sb_loss')} {baht(Math.abs(pv.profit))}</b>
            {/* i18next picks singular / plural off `count` — the English pluralisation that used to
                be spliced in here ("entry is" / "entries are") has no equivalent in Thai or Chinese
                and must not be re-created by hand in either. */}
            {pv.drafts ? <><br /><span style={{ color: '#B45309' }}>{t('pa_sb_close_drafts', { count: pv.drafts })}</span></> : null}
            {pv.outOfRange ? <><br /><span style={{ color: '#B42318' }}>{t('pa_sb_close_outofrange', { count: pv.outOfRange })}</span></> : null}
            {!pv.balanced ? <><br /><span style={{ color: '#B42318' }}>{t('pa_sb_close_unbalanced')}</span></> : null}
          </div>
          <div style={{ display: 'flex', gap: 7, marginTop: 10 }}>
            <button onClick={close} disabled={busy || !!pv.drafts || !!pv.outOfRange || !pv.balanced}
              style={{ ...actionStyle, background: '#B42318', color: '#fff', border: 'none', opacity: (busy || pv.drafts || pv.outOfRange || !pv.balanced) ? 0.5 : 1 }}>
              <i className={busy ? 'ti ti-loader' : 'ti ti-lock'} aria-hidden /> {t('pa_sb_close_it')}
            </button>
            <button onClick={() => setPv(null)} style={actionStyle}>{t('cancel')}</button>
          </div>
        </div>
      )}
      {err && <div style={{ marginTop: 8, fontSize: 12.5, color: '#B42318' }}>{err}</div>}
    </div>
  );
}

/** The five statements as ONE document — cover, contents with real page numbers, fixed order.
 *
 *  This sits ABOVE the individual statement cards on purpose. The bound set is what a client,
 *  the Revenue Department and an auditor are each handed; the single statements below it are what
 *  an accountant opens while working. Putting the deliverable first is the whole point of binding
 *  it, and five download buttons with no "all of it" above them is what we had instead. */
// `th` is the statutory statement name and stays as it is in every locale; `glossKey` is the
// translatable description beside it. Two fields, not one bilingual string, because only one of
// them may be translated.
export const BOUND_OPTIONS: { key: string; th: string; glossKey: string; appendix?: boolean }[] = [
  { key: 'sfp', th: 'งบแสดงฐานะการเงิน', glossKey: 'pa_fs_sfp' },
  { key: 'pl', th: 'งบกำไรขาดทุน', glossKey: 'pa_fs_pl' },
  { key: 'sce', th: 'งบแสดงการเปลี่ยนแปลงส่วนของเจ้าของ', glossKey: 'pa_fs_sce' },
  { key: 'cf', th: 'งบกระแสเงินสด', glossKey: 'pa_fs_cf' },
  { key: 'notes', th: 'หมายเหตุประกอบงบการเงิน', glossKey: 'pa_fs_notes' },
  { key: 'wa', th: 'บัญชีทำการ', glossKey: 'pa_fs_wa', appendix: true },
];

export function BoundSet({ periodId, reload }: { periodId: string; reload: number }) {
  const { t } = useTranslation();
  // The five by default. The working account is opt-in: it is a Revenue Code schedule, and a set
  // sent to a client who asked for their financial statements should not quietly carry one.
  const [picked, setPicked] = useState<string[]>(['sfp', 'pl', 'sce', 'cf', 'notes']);
  const [sum, setSum] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Ordered by the OPTION list, never by click order — the server binds in its own sequence and the
  // screen must not imply otherwise.
  const include = BOUND_OPTIONS.filter(o => picked.includes(o.key)).map(o => o.key).join(',');

  useEffect(() => {
    if (!periodId || !include) { setSum(null); return; }
    let alive = true;
    api.ledgerBoundSummary(periodId, include)
      .then((d: any) => { if (alive) setSum(d); })
      // A failed pre-build check must not pass as a clean one: say so, and keep the DRAFT/issues
      // hidden rather than pretending there are none.
      .catch((e: any) => { if (alive) { setSum(null); setErr(String(e?.message || 'Could not check the period before building')); } });
    return () => { alive = false; };
  }, [periodId, include, reload]);

  const build = async () => {
    if (busy || !include) return;
    setBusy(true); setErr('');
    try {
      const url = await api.ledgerBookUrl('bound', periodId, { include });
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  const toggle = (k: string) => setPicked(ps => (ps.includes(k) ? ps.filter(x => x !== k) : [...ps, k]));

  return (
    <div style={{
      border: `1.6px solid ${sum?.draft ? '#F1C232' : 'var(--accent)'}`, borderRadius: 11, padding: '13px 15px',
      background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <i className="ti ti-book-2" style={{ fontSize: 21, color: 'var(--accent)' }} aria-hidden />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>
            งบการเงิน (ฉบับรวมเล่ม)
            {sum?.draft && (
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, letterSpacing: '.14em', color: '#B42318', border: '1px solid #B42318', borderRadius: 3, padding: '1px 6px' }}>
                ฉบับร่าง · DRAFT
              </span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
            {t('pa_fs_bound_what')}
            {sum ? ` · ${dmy(sum.from)} – ${dmy(sum.to)}` : ''}
          </div>
        </div>
        <button onClick={build} disabled={busy || !include}
          style={{ ...actionStyle, background: 'var(--accent)', color: '#fff', border: 'none', opacity: (busy || !include) ? 0.55 : 1 }}>
          {/* Building this one measures the pagination, so it is seconds rather than instant —
              the spinner is not decoration. */}
          <i className={busy ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden />
          {' '}{busy ? t('pa_fs_bound_building') : t('pa_fs_bound_build')}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 13, flexWrap: 'wrap' }}>
        {BOUND_OPTIONS.map(o => (
          <label key={o.key} title={t(o.glossKey)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={picked.includes(o.key)} onChange={() => toggle(o.key)} />
            <span>{o.th}{o.appendix ? <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}> (ภาคผนวก)</span> : null}</span>
          </label>
        ))}
      </div>

      {/* The same issues the cover will carry, shown here first — so nobody emails a set whose
          books do not balance and finds out from the client.
          ⚠️ i.th / i.en come from the SERVER, which is what will actually be printed on the cover.
          They are data, not UI text: translating them here would show the operator one wording and
          print another. */}
      {!!sum?.issues?.length && (
        <div style={{ border: '1px solid #F3B9B9', background: '#FCEBEB', borderRadius: 8, padding: '8px 11px', fontSize: 11.5, color: '#7A1A12', lineHeight: 1.7 }}>
          <div style={{ fontWeight: 800, marginBottom: 2 }}>⚠ {t('pa_fs_cover_issues')}</div>
          {sum.issues.map((i: any, n: number) => (
            <div key={n}>• {i.th} <span style={{ color: '#9A4A42' }}>{i.en}</span></div>
          ))}
        </div>
      )}
      {!picked.length && <div style={{ fontSize: 11.5, color: '#92400E' }}>{t('pa_fs_pick_least')}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318' }}>{err}</div>}
    </div>
  );
}

export default function StatutoryBooks({ clientId, clientName }: { clientId: string; clientName: string }) {
  const { t } = useTranslation();
  const [periods, setPeriods] = useState<Period[] | null>(null);
  const [periodId, setPeriodId] = useState('');
  const [books, setBooks] = useState<Book[] | null>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    api.ledgerPeriods(clientId)
      .then((d: any) => { if (!alive) return; const ps = d.periods || []; setPeriods(ps); setPeriodId(ps[0]?.id || ''); })
      .catch((e: any) => { if (alive) { setPeriods([]); setErr(e.message); } });
    return () => { alive = false; };
  }, [clientId]);

  // `reload` bumps after a draft is posted: the new entry belongs in a journal, and a book list
  // that still says "nothing posted" would be lying about work the operator just did.
  const [reload, setReload] = useState(0);
  const [opening, setOpening] = useState(false);
  useEffect(() => {
    if (!periodId) { setBooks(null); return; }
    let alive = true;
    api.ledgerAvailable(periodId)
      .then((d: any) => { if (alive) setBooks(d.books || []); })
      .catch(() => { if (alive) setBooks([]); });
    return () => { alive = false; };
  }, [periodId, reload]);

  // The statements' headline figures, so the screen can show the result and any warning without
  // building a PDF first — and so an out-of-balance period is visible before anyone prints it.
  const [fs, setFs] = useState<any>(null);
  useEffect(() => {
    if (!periodId) { setFs(null); return; }
    let alive = true;
    api.ledgerStatements(periodId).then((d: any) => { if (alive) setFs(d); }).catch(() => { if (alive) setFs(null); });
    return () => { alive = false; };
  }, [periodId, reload]);

  // The extras routes are client-scoped as well as period-scoped, so they take their own opener
  // rather than bending ledgerBookUrl into carrying a clientId it never needed.
  const openExtra = async (label: string, path: string, extra: Record<string, string> = {}) => {
    if (busy) return;
    setBusy(label); setErr('');
    try {
      const url = await api.axDocUrl(path, { clientId, periodId, ...extra });
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const open = async (label: string, path: string, extra: Record<string, string> = {}) => {
    if (busy) return;
    setBusy(label); setErr('');
    try {
      const url = await api.ledgerBookUrl(path, periodId, extra);
      // Opened before the revoke, and the revoke is generous: a large general ledger can take a
      // moment to paint, and revoking early leaves the reader with a blank tab.
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  if (periods === null) return <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;

  const refreshPeriods = () => {
    api.ledgerPeriods(clientId).then((d: any) => { const ps = d.periods || []; setPeriods(ps); setPeriodId(ps[0]?.id || ''); setReload(n => n + 1); });
  };

  if (!periods.length) return <OpenPeriodForm clientId={clientId} onOpened={refreshPeriods} />;

  const p = periods.find(x => x.id === periodId);
  // Journals only: the general ledger and the trial balance are period-wide and always offered.
  const journals = (books || []).filter(b => b.kind !== 'gl');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_sb_period_label')}</label>
        <select value={periodId} onChange={e => setPeriodId(e.target.value)}
          style={{ padding: '7px 10px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }}>
          {periods.map(x => (
            <option key={x.id} value={x.id}>
              {dmy(x.start_date)} – {dmy(x.end_date)} · {t('pa_sb_n_entries', { count: x.entries })}
              {x.status === 'closed' ? ` · ${t('pa_sb_status_closed')}` : ''}
            </option>
          ))}
        </select>
        {p?.status === 'closed' && (
          <span style={{ fontSize: 11, fontWeight: 800, color: '#166534', background: '#E8F6EE', border: '1px solid #BBE5CB', padding: '2px 9px', borderRadius: 999 }}>
            ปิดบัญชีแล้ว
          </span>
        )}
        {/* Once every period is closed the client still needs the next one, or the books stop. */}
        {periods.every(x => x.status === 'closed') && (
          <button onClick={() => setOpening(o => !o)} style={{ ...actionStyle, padding: '5px 10px', fontSize: 12 }}>
            <i className="ti ti-plus" aria-hidden /> {t('pa_sb_open_next')}
          </button>
        )}
      </div>

      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', padding: '8px 12px', borderRadius: 8 }}>{err}</div>}

      {opening && <OpenPeriodForm clientId={clientId} onOpened={() => { setOpening(false); refreshPeriods(); }} />}

      <EntityInfo clientId={clientId} periodClosed={p?.status === 'closed'} onChanged={() => setReload(n => n + 1)} />

      {periodId && <CompliancePanel clientId={clientId} periodId={periodId} />}

      {periodId && <TaxLedgers clientId={clientId} periodId={periodId} periodClosed={p?.status === 'closed'}
        onChanged={() => setReload(n => n + 1)} />}

      {periodId && <Registers clientId={clientId} periodId={periodId} periodClosed={p?.status === 'closed'}
        onChanged={() => setReload(n => n + 1)} />}

      {periodId && <Headless clientId={clientId} periodId={periodId} periodClosed={p?.status === 'closed'} />}

      <VendorAccounts clientId={clientId} />
      <ChartOfAccounts clientId={clientId} />

      {periodId && <FixedAssets clientId={clientId} periodId={periodId} periodClosed={p?.status === 'closed'}
        onChanged={() => setReload(n => n + 1)} />}

      {periodId && <ManualEntry clientId={clientId} periodId={periodId} periodClosed={p?.status === 'closed'}
        onChanged={() => setReload(n => n + 1)} />}

      {periodId && <DraftEntries clientId={clientId} periodId={periodId} periodClosed={p?.status === 'closed'}
        refreshKey={reload} onPosted={() => setReload(n => n + 1)} />}

      {periodId && p?.status !== 'closed' && <ClosePeriod periodId={periodId} onClosed={refreshPeriods} />}

      {books && !journals.length ? (
        <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
          {t('pa_sb_nothing_posted')}
        </div>
      ) : (
        <>
          <div>
            {/* สมุดรายวัน is the statutory name of the daily journals, so it stays; only the gloss
                after it is translated, and in the Thai locale the gloss is empty and drops out. */}
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 7 }}>
              สมุดรายวัน · {t('pa_sb_h_journals')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 8 }}>
              {journals.map(b => {
                // A bank book is required per account NUMBER, so each account is its own volume and
                // gets its own card — that is the Act's shape, not a UI choice.
                const key = b.kind + b.bankAccountId;
                const label = statutoryBookName(b.kind) || b.label || b.kind;
                return (
                  <button key={key} style={{ ...card, opacity: busy === key ? 0.6 : 1 }}
                    onClick={() => open(key, `journal/${b.kind}`, b.bankAccountId ? { bankAccountId: b.bankAccountId } : {})}>
                    <i className={busy === key ? 'ti ti-loader' : 'ti ti-book'} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{label}</span>
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {/* b.label is the server's own description of the volume — data, not UI
                            text. Only the count unit is translated. */}
                        {b.label}{b.bankAccountId ? ` · ${b.bankAccountId}` : ''} · {t('pa_sb_n_entries', { count: b.entries })}
                      </span>
                    </span>
                    <i className="ti ti-file-type-pdf" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 7 }}>
              บัญชีแยกประเภท · {t('pa_sb_h_ledger')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 8 }}>
              {/* ⛔ The third element is a KEY, not a translated string. These arrays are `as const`,
                  so the inferred tuple type is what makes `open(k, path)` type-check — calling t()
                  inside the array would widen it and break the destructuring below. */}
              {([['gl', 'บัญชีแยกประเภท', 'pa_sb_c_gl', 'general-ledger'],
                 ['tb', 'งบทดลอง', 'pa_sb_c_tb', 'trial-balance'],
                 // The subsidiary ledgers print their own reconciliation against the control
                 // account, so an unattributed line shows up on the page rather than in nobody's view.
                 ['ar', 'บัญชีย่อยลูกหนี้', 'pa_sb_c_ar', 'subsidiary/ar'],
                 ['ap', 'บัญชีย่อยเจ้าหนี้', 'pa_sb_c_ap', 'subsidiary/ap']] as const).map(([k, th, enKey, path]) => (
                <button key={k} style={{ ...card, opacity: busy === k ? 0.6 : 1 }} onClick={() => open(k, path)}>
                  <i className={busy === k ? 'ti ti-loader' : 'ti ti-list-details'} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{th}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{t(enKey)}</span>
                  </span>
                  <i className="ti ti-file-type-pdf" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
                </button>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 7 }}>
              งบการเงิน · {t('pa_sb_h_statements')}
            </div>
            {fs && (
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                <span>{t('pa_sb_pl_line', { income: baht(fs.pl?.totals?.income), expense: baht(fs.pl?.totals?.expense) })} = <b style={{ color: 'var(--text-primary)' }}>{baht(fs.pl?.totals?.profit)}</b></span>
                <span>{t('pa_sb_assets_line', { amount: baht(fs.sfp?.totals?.assets) })}</span>
                {/* An unbalanced balance sheet is stated on the screen, not left for whoever opens the PDF. */}
                {fs.sfp && !fs.sfp.balanced && (
                  <span style={{ color: '#B42318', fontWeight: 700 }}>
                    <i className="ti ti-alert-triangle" aria-hidden /> งบไม่ดุล — {t('pa_sb_unbalanced_why')}
                  </span>
                )}
                {(fs.sfp?.warnings || []).filter((w: string) => !/ไม่ดุล/.test(w)).map((w: string, i: number) => (
                  <span key={i} style={{ color: '#92400E' }}><i className="ti ti-info-circle" aria-hidden /> {w}</span>
                ))}
              </div>
            )}
            <BoundSet periodId={periodId} reload={reload} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 8 }}>
              {([['sfp', 'งบแสดงฐานะการเงิน', 'pa_fs_sfp'],
                 ['pl', 'งบกำไรขาดทุน', 'pa_fs_pl'],
                 ['sce', 'งบแสดงการเปลี่ยนแปลงส่วนของเจ้าของ', 'pa_fs_sce']] as const).map(([k, th, enKey]) => (
                <button key={k} style={{ ...card, opacity: busy === k ? 0.6 : 1 }} onClick={() => open(k, `statement/${k}`)}>
                  <i className={busy === k ? 'ti ti-loader' : 'ti ti-report-analytics'} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{th}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{t(enKey)}</span>
                  </span>
                  <i className="ti ti-file-type-pdf" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
                </button>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 7 }}>
              เอกสารประกอบ · {t('pa_sb_h_extras')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 8 }}>
              {([['cf', 'งบกระแสเงินสด', 'pa_fs_cf', 'cash-flow.pdf', {}],
                 ['notes', 'หมายเหตุประกอบงบการเงิน', 'pa_fs_notes', 'notes.pdf', {}],
                 ['stock', 'บัญชีสินค้า', 'pa_sb_c_stock', 'stock/account', {}],
                 ['goods', 'รายงานสินค้าและวัตถุดิบ', 'pa_sb_c_goods', 'stock/goods-report', { from: `${new Date().getFullYear()}-01-01`, to: `${new Date().getFullYear()}-12-31` }],
                 ['vatout', 'รายงานภาษีขาย', 'pa_sb_c_vatout', 'vat/report', { section: 'output', year: String(new Date().getFullYear()), month: String(new Date().getMonth() + 1) }],
                 ['vatin', 'รายงานภาษีซื้อ', 'pa_sb_c_vatin', 'vat/report', { section: 'input', year: String(new Date().getFullYear()), month: String(new Date().getMonth() + 1) }],
                ] as const).map(([k, th, enKey, path, extra]) => (
                <button key={k} style={{ ...card, opacity: busy === k ? 0.6 : 1 }} onClick={() => openExtra(k, path as string, extra as Record<string, string>)}>
                  <i className={busy === k ? 'ti ti-loader' : 'ti ti-file-text'} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{th}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{t(enKey)}</span>
                  </span>
                  <i className="ti ti-file-type-pdf" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
                </button>
              ))}
            </div>
          </div>

          {/* ⚠️ The second sentence states what these statements are NOT, in law. Its numbers, its
              form reference (รายการย่อ) and its two notification years are facts and stay exactly as
              written in every locale — the sentence around them is what gets translated. */}
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
            {t('pa_sb_foot_books', { client: clientName })}
            <br />
            {t('pa_sb_foot_statements')}
          </div>
        </>
      )}
    </div>
  );
}
